import { useRef, useEffect, useState, useCallback } from 'react';
import { useAppSelector } from 'src/store/store';

interface UseDualStreamCaptureReturn {
  lowResVideoRef: React.RefObject<HTMLVideoElement>;
  highResVideoRef: React.RefObject<HTMLVideoElement>;
  captureHighResFrame: () => Promise<File>;
  streamsReady: boolean;
  startStreams: (signal?: AbortSignal) => Promise<void>;
  stopStreams: () => void;
}

/** Attach a MediaStream to a video element and start playback */
const attachStream = async (ref: React.RefObject<HTMLVideoElement>, stream: MediaStream) => {
  if (ref.current) {
    ref.current.srcObject = stream;
    await ref.current.play();
  }
};

/** Stop all tracks on a stream (safe to call with null) */
const releaseStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach(t => t.stop());
};

/**
 * Hook to manage dual camera streams with single camera fallback.
 *
 * Streams are NOT auto-initialized on mount. Call `startStreams()` to begin
 * and `stopStreams()` to release camera resources. Both are idempotent.
 */
export const useDualStreamCapture = (
  lowResScale: number = 0.5,
  highResScale: number = 1.0
): UseDualStreamCaptureReturn => {
  const lowResVideoRef = useRef<HTMLVideoElement>(null);
  const highResVideoRef = useRef<HTMLVideoElement>(null);
  const [streamsReady, setStreamsReady] = useState(false);

  const lowResStreamRef = useRef<MediaStream | null>(null);
  const highResStreamRef = useRef<MediaStream | null>(null);

  // Persistent canvas for high-res frame capture — reused to prevent flash
  const highResCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const highResContextRef = useRef<CanvasRenderingContext2D | null>(null);

  const { screenWidth, screenHeight, selectedCameraDeviceId } = useAppSelector((state) => state.inspectionCapture);

  /** Stop all active media tracks. Idempotent. */
  const stopStreams = useCallback((): void => {
    if (!lowResStreamRef.current && !highResStreamRef.current) {
      return;
    }

    releaseStream(lowResStreamRef.current);
    releaseStream(highResStreamRef.current);
    lowResStreamRef.current = null;
    highResStreamRef.current = null;

    if (lowResVideoRef.current) {
      lowResVideoRef.current.srcObject = null;
    }
    if (highResVideoRef.current) {
      highResVideoRef.current.srcObject = null;
    }

    setStreamsReady(false);
  }, []);

  /** Initialize camera streams. Idempotent. Pass AbortSignal for safe cancellation. */
  const startStreams = useCallback(async (signal?: AbortSignal): Promise<void> => {
    // Already active — nothing to do
    if (lowResStreamRef.current || highResStreamRef.current) {
      return;
    }

    const baseWidth = screenWidth || 2560;
    const baseHeight = screenHeight || 1440;

    const buildConstraints = (w: number, h: number): MediaTrackConstraints => {
      const c: MediaTrackConstraints = { width: { ideal: w }, height: { ideal: h } };
      if (selectedCameraDeviceId) {
        c.deviceId = { exact: selectedCameraDeviceId };
      } else {
        c.facingMode = 'environment';
      }
      return c;
    };

    const lowResConstraints = buildConstraints(Math.floor(baseWidth * lowResScale), Math.floor(baseHeight * lowResScale));
    const highResConstraints = buildConstraints(Math.floor(baseWidth * highResScale), Math.floor(baseHeight * highResScale));

    try {
      // Try dual camera first, fall back to single
      let lowResStream: MediaStream;
      let highResStream: MediaStream;

      try {
        lowResStream = await navigator.mediaDevices.getUserMedia({ video: lowResConstraints });
        if (signal?.aborted) {
          releaseStream(lowResStream);
          return;
        }

        highResStream = await navigator.mediaDevices.getUserMedia({ video: highResConstraints });
        if (signal?.aborted) {
          releaseStream(lowResStream);
          releaseStream(highResStream);
          return;
        }
      } catch {
        // Dual failed — release any acquired stream before fallback
        releaseStream(lowResStream!);

        if (signal?.aborted) {
          return;
        }
        const single = await navigator.mediaDevices.getUserMedia({ video: highResConstraints });
        if (signal?.aborted) {
          releaseStream(single);
          return;
        }
        lowResStream = single;
        highResStream = single;
      }

      lowResStreamRef.current = lowResStream;
      highResStreamRef.current = highResStream;
      await attachStream(lowResVideoRef, lowResStream);
      await attachStream(highResVideoRef, highResStream);
      setStreamsReady(true);
    } catch (error) {
      console.error('❌ Camera init failed:', error);
      stopStreams();
    }
  }, [lowResScale, highResScale, selectedCameraDeviceId, stopStreams]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopStreams();
  }, [stopStreams]);

  /** Capture high-res frame as File for photo capture */
  const captureHighResFrame = async (): Promise<File> => {
    const video = highResVideoRef.current;
    if (!video) {
      throw new Error('High-res video stream not available');
    }

    if (!highResCanvasRef.current) {
      highResCanvasRef.current = document.createElement('canvas');
      highResContextRef.current = highResCanvasRef.current.getContext('2d');
    }
    const canvas = highResCanvasRef.current;
    const ctx = highResContextRef.current;
    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    ctx.drawImage(video, 0, 0);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => b ? resolve(b) : reject(new Error('Failed to create blob from canvas')),
        'image/jpeg',
        0.95
      );
    });
    return new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
  };

  return {
    lowResVideoRef,
    highResVideoRef,
    captureHighResFrame,
    streamsReady,
    startStreams,
    stopStreams,
  };
};
